import JEN_HowSFWork_Animation from '@/assets/animation/JEN_HowSFWork-Animation_Web--v04.json';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/Accordion';
import { PauseIcon, PlayIcon } from '@/components/Icons';
import StyleFilterDetailCard from '@/components/StyleFilterDetailCard/StyleFilterDetailCard';
import { TrackList } from '@/components/TrackList';
import useInitializeData from '@/hooks/useInitializeProject';
import { getStyleFilterGroupDetailByName } from '@/service/filter';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useTrackLists } from '@/state/style-filter-list-store';
import { cn } from '@/utils';
import { useRequest } from 'ahooks';
import Lottie from 'lottie-react';
import { useParams } from 'react-router-dom';
import StyleFilterDetailDialogContent from '../desktop/components/StyleFilterDetailDialogContent';
import { useBuyStyleFilter } from '../hooks/useBuyStyleFilter';
import BackLastPage from '../mobile/components/BackLastPage';
import StrengthToggle from '../mobile/components/StrengthSelector/StrengthToggle';
import NavBar from '../mobile/NavBar';
import { kebabToTitleCase } from '@/utils';
import { ArtistLink } from '../desktop/components/ArtistLink';
import { checkAuthAndRedirect } from '@/hooks/useAuthCheck';
import { FormProvider } from 'react-hook-form';
import { useModel } from '@/pages/Home/Generator/model';
import { useDimensions } from '@/hooks/useDimensions';

const MarketStyleFilterDetail = () => {
  const { filterName } = useParams<{ filterName: string }>();
  const { isMobileOrPad } = useInitializeData();
  const { marketAudioListData } = useTrackLists();

  const { form } = useModel();

  const { data } = useRequest(() => getStyleFilterGroupDetailByName(kebabToTitleCase(filterName ?? '')));
  const { play, playAudioInfo, isPlaying, pause, stop } = useAudioPlayerStore();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { selectedFilter, handleSwitch, handleBuy } = useBuyStyleFilter(data?.data || { sf_list: [] });

  const isCurrentPlaying = playAudioInfo?.id === String(data?.data?.id) && isPlaying;

  if (!data?.data || !selectedFilter) {
    return null;
  }

  const sfDetail = data.data;

  if (isMobileOrPad) {
    return (
      <FormProvider {...form}>
        <NavBar>
          <div className="p-6 bg-white">
            <BackLastPage />
            <div className="header">
              <div className="flex items-center mb-[12px] mt-[28px]">
                <ArtistLink
                  artist={sfDetail}
                  textClassName="text-[#111112] text-[18px]"
                  avatarClassName="w-[26px] h-[26px] border-jen-interactive-primary"
                />
              </div>
              <div className="text-[#111112] text-[32px] tracking-[-0.32px] leading-[32px] ">{sfDetail.name}</div>
              <div
                className="py-3 flex gap-2 text-jen-interactive-primary items-center cursor-pointer hover:opacity-90"
                onClick={() => {
                  stop();
                  if (isCurrentPlaying) {
                    pause();
                  } else {
                    play({
                      url: sfDetail.sf_list[selectedFilter.key === 'high' ? 1 : 0].audio,
                      name: sfDetail.name,
                      id: String(sfDetail.id),
                    });
                  }
                }}
              >
                <div
                  className="flex justify-center items-center w-9 h-9 rounded-full"
                  style={{
                    background: ' var(--Colors-Orange-10, rgba(255, 91, 39, 0.10))',
                  }}
                >
                  {isCurrentPlaying ? <PauseIcon width={14} fillColor="#ff5b27" /> : <PlayIcon width={14} fillColor="#ff5b27" />}
                </div>
                <span className="ml-2 tracking-[-3%]">
                  Preview StyleFilter<sup className="top-[-6px] text-[6px]">TM</sup>&nbsp;Track
                </span>
              </div>
            </div>
            <div className="w-full flex justify-center rounded-2xl py-16 overflow-hidden">
              <div className="flex flex-col">
                <StyleFilterAlbum
                  sfDetail={sfDetail}
                  handleSwitch={handleSwitch}
                  defaultValue={selectedFilter.key === 'high' ? 'HIGH' : 'LOW'}
                />
              </div>
            </div>
            <div className="w-full">
              <div className="buy">
                <div className="text-jen-content-primary text-[20px] mb-[16px] py-4 tracking-[-4%]">
                  Choose between low or high strength to infuse more or less of the song style into your jeneration:
                </div>
                <div className="w-full max-w-2xl">
                  <StrengthToggle
                    options={sfDetail.sf_list.map((item) => ({
                      label: item.name,
                      price: Number(item.price),
                      value: item.id.toString(),
                    }))}
                    defaultValue={selectedFilter.id.toString()}
                    onChange={(value) => {
                      const selectedItem = sfDetail.sf_list.find((item) => item.id.toString() === value);
                      const strengthValue = selectedItem?.key === 'high' ? 1 : 0;
                      handleSwitch({
                        value: strengthValue,
                        id: Number(value),
                      });
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    checkAuthAndRedirect();
                    handleBuy();
                  }}
                  className={cn(
                    'mt-6 w-full cursor-pointer leading-4 text-jen-content-primary-contrast rounded-md bg-jen-interactive-primary py-[14px] px-[16px] hover:opacity-90',
                  )}
                >
                  Purchase ${selectedFilter?.price}
                </button>
                <div className="text-jen-content-secondary text-[16px] mt-3">
                  90 Min of {selectedFilter.key === 'high' ? 'High' : 'Low'} Strength Jeneration
                </div>
              </div>
              <div className="detail mt-4">
                <StyleFilterAccordion />
              </div>
              <div className="mt-4">
                <section className="p-4 bg-jen-background-primary rounded-xl overflow-hidden border border-[#202223]/10 px-4 ">
                  <h2 className="text-[24px] text-[#48484b] tracking-[-0.96px] leading-10 mb-6">
                    Tracks Jenerated with
                    <div>
                      <span>Imogen Heap </span>StyleFilters™
                    </div>
                  </h2>
                  <TrackList
                    trackListData={marketAudioListData[`${sfDetail.sf_list[0].code}`]}
                    label={{ title: 'Title', creator: 'User', filter: 'StyleFilter' }}
                  />
                </section>
              </div>
            </div>
          </div>
        </NavBar>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...form}>
      <StyleFilterDetailDialogContent id={data?.data?.id} />
    </FormProvider>
  );
};

const StyleFilterAlbum = ({
  sfDetail,
  handleSwitch,
  defaultValue = 'LOW',
}: {
  sfDetail: FilterGroup;
  handleSwitch?: ({ value, id }: { value: number; id: number | undefined }) => void;
  defaultValue?: 'LOW' | 'HIGH' | 'NONE';
}): JSX.Element => {
  const { width } = useDimensions();

  return (
    <StyleFilterDetailCard
      onRemove={() => undefined}
      onUse={() => undefined}
      title={sfDetail?.artist}
      content={sfDetail?.name}
      sfId={sfDetail?.id}
      background={sfDetail?.album_cover}
      showButtons={false}
      disabled={false}
      isMarket={true}
      onSwitch={handleSwitch}
      defaultValue={defaultValue}
      coverStyle={{
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: `${width * (170 / 360)}px`,
        height: `${width * (170 / 360) * (276 / 170)}px`,
        boxShadow: `34.208px 64.614px 20.525px 0px rgba(0, 0, 0, 0.00), 22.045px 41.809px 19.004px 0px rgba(0, 0, 0, 0.03), 12.163px 23.565px 15.964px 0px rgba(0, 0, 0, 0.10), 5.321px 10.642px 11.403px 0px rgba(0, 0, 0, 0.17), 1.52px 2.281px 6.081px 0px rgba(0, 0, 0, 0.20)`,
      }}
      switchDisabled={false}
      className="w-full h-full"
    />
  );
};

const StyleFilterAccordion = () => {
  return (
    <Accordion type="multiple" className="w-full">
      <AccordionItem value="item-1" className=" border-b border-t border-[#CECFD3] py-4">
        <AccordionTrigger className=" text-[#48484b]">
          <div>
            How StyleFilters<sup className=" inline top-[-6px] text-[6px]">TM</sup> Work
          </div>
        </AccordionTrigger>
        <AccordionContent className="mt-[24px]">
          <Lottie animationData={JEN_HowSFWork_Animation} className="" />
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="item-2" className="border-b border-[#CECFD3] py-4">
        <AccordionTrigger className=" text-[#48484b]">
          <div>
            StyleFilters<sup className=" inline top-[-6px] text-[6px]">TM</sup>Jenerated Usage
          </div>
        </AccordionTrigger>
        <AccordionContent className=" text-jen-content-tertiary text-[16px] leading-[19px] mt-[16px]">
          Tracks created with StyleFilters™ are fully owned by the user—ready to be used, released, and shared across any project—but may
          not be used to train other AI models.
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default MarketStyleFilterDetail;
